import { toNumber } from 'lodash';
import { Condition } from '@upstart/harmony/lib/constants';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { CarCard } from '@upstart/harmony/lib/legos/carTypes';
import { generateProdigyVIN } from '@upstart/harmony/lib/car-utils';
import React, { ChangeEvent, FormEvent, useMemo, useState } from 'react';
import { isEmbeddedInCommandCenter } from 'util/commandCenter';
import YMMTService from '../../services/YMMTService';
import FloatingSelect from '../FloatingSelect';
import { useCallablePromise } from '../../hooks/useCallablePromise';
import FloatingInput from '../FloatingInput';
import './AddVehicleWithoutVin.scss';
import DecodeService from '../../services/DecodeService';
import CarImage from '../CarImage';
import CarService from '../../services/CarService';

type Props = {
  onClose: () => void;
  onCarCreation: (car: CarCard) => void;
  dealer?: Dealer;
};

function AddVehicleWithoutVin({ onClose, dealer, onCarCreation }: Props) {
  const [data, setData] = useState({
    year: '',
    make: '',
    manualModel: '',
    condition: Condition.NEW,
    manualTrim: '',
    model: '',
    trimIndex: '',
    price: '',
    msrp: '',
  });

  const [isProcessing, setProcessing] = useState(false);

  const yearsData = useCallablePromise({
    promiseFn: YMMTService.getYears,
    args: [{ condition: data.condition }],
    deps: [data.condition],
    shouldExecute: !!data.condition,
  });

  const makesData = useCallablePromise({
    promiseFn: YMMTService.getMakes,
    args: [{ condition: data.condition, year: data.year }],
    deps: [data.year],
    shouldExecute: !!data.year,
  });
  const modelsData = useCallablePromise({
    promiseFn: YMMTService.getModels,
    args: [{ condition: data.condition, year: data.year, make: data.make }],
    deps: [data.year, data.make],
    shouldExecute: !!(data.year && data.make),
  });
  const trimsData = useCallablePromise({
    promiseFn: YMMTService.getTrims,
    args: [
      {
        condition: data.condition,
        year: data.year,
        make: data.make,
        model: data.model,
      },
    ],
    deps: [data.year, data.make, data.model],
    shouldExecute: !!(data.year && data.make && data.model),
  });

  const carData = useCallablePromise({
    promiseFn: DecodeService.decodeWithoutVin,
    args: [{ year: data.year, make: data.make, model: data.model }],
    deps: [data.year, data.make, data.model],
    shouldExecute: !!(data.year && data.make && data.model),
    resetOnChange: true,
  });

  const modelIsUnavailable = useMemo(
    () =>
      !!yearsData.data &&
      !!makesData.data &&
      !modelsData.isLoading &&
      !modelsData.data?.length,
    [data.make, data.year, modelsData, makesData.data, yearsData.data],
  );

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const newState = { ...data, [e.target.name]: e.target.value };
    if (name === 'year') {
      newState.make = '';
      newState.model = '';
      newState.trimIndex = '';
      if (newState.year)
        newState.condition =
          toNumber(newState.year) >= new Date().getFullYear()
            ? Condition.NEW
            : Condition.USED;
    } else if (name === 'make') {
      newState.model = '';
      newState.trimIndex = '';
    } else if (name === 'model') {
      newState.trimIndex = '';
    }
    setData(newState);
  };

  const getCar = () => {
    const msrp = parseFloat(data.msrp);

    const residualValues = Object.values(
      dealer?.inStoreAprConfig?.New?.lease?.terms || {},
    ).reduce((acc, t) => {
      if (t?.term) acc[t.term] = msrp / 2;
      return acc;
    }, {} as { [key: string]: number });

    const model = modelIsUnavailable ? data.manualModel : data.model;

    let trim;
    let modelCode;
    let marketScanVehicleId;

    if (modelIsUnavailable) {
      trim = data.manualTrim;
    } else {
      const trimOption = trimsData.data?.[+data.trimIndex];
      trim = trimOption?.trim || trimOption?.name;
      marketScanVehicleId = useMarketScanIncentives ? trimOption?.id : null;
      modelCode = trimsData.data?.[+data.trimIndex]?.modelCode;
    }
    return {
      id: generateProdigyVIN(),
      year: parseInt(data.year, 10),
      name: `${data.year} ${data.make} ${model}`,
      make: data.make,
      condition: data.condition ?? Condition.NEW,
      residualValues,
      carImg: carData.data?.carImg,
      model,
      mileage: 0,
      trim,
      ...(modelCode && {
        modelCode,
      }),
      msrp,
      marketScanVehicleId,
      price: parseFloat(data.price),
    };
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setProcessing(true);
    const newCar = await CarService.create(getCar());
    const car = await CarService.getCar(newCar.id);
    setProcessing(false);
    onCarCreation(car);
  };

  const useMarketScanIncentives = !!dealer?.marketScanConfig?.incentivesEnabled;
  const optionObjectValueName = useMarketScanIncentives ? 'id' : 'name';

  return (
    <form className={'vehicle-without-vin'} onSubmit={onSubmit}>
      <h5 className={'vin-search-header'}>Please enter a vehicle to add</h5>
      {!useMarketScanIncentives && (
        <div className="add-vehicle-row car-image">
          <CarImage car={carData.data} />
        </div>
      )}
      <div className="add-vehicle-row">
        <div className={'add-vehicle-half-item'}>
          <FloatingSelect
            name="condition"
            label="Condition"
            onChange={onChange}
            value={data.condition}
            options={Object.values(Condition)}
            required
          />
        </div>
        <div className={'add-vehicle-half-item'} />
      </div>
      <div className="add-vehicle-row">
        <div className="add-vehicle-half-item">
          <FloatingSelect
            name="year"
            label="Year"
            disabled={!data.condition}
            onChange={onChange}
            value={data.year}
            options={yearsData.data?.map(r => r.name) || []}
            required
          />
        </div>
        <div className="add-vehicle-half-item">
          <FloatingSelect
            name="make"
            label="Make"
            disabled={!data.year}
            onChange={onChange}
            value={data.make}
            options={makesData.data || []}
            required
            optionObjectKeyName="name"
            optionObjectValueName={optionObjectValueName}
          />
        </div>
      </div>
      {modelIsUnavailable ? (
        <div className="add-vehicle-row">
          <div className="add-vehicle-half-item">
            <FloatingInput
              name="manualModel"
              label="Model"
              onChange={onChange}
              value={data.manualModel}
            />
          </div>
          <div className="add-vehicle-half-item">
            <FloatingInput
              name="manualTrim"
              label="Trim"
              onChange={onChange}
              value={data.manualTrim}
            />
          </div>
        </div>
      ) : (
        <div className="add-vehicle-row">
          <div className="add-vehicle-half-item">
            <FloatingSelect
              name="model"
              label="Model"
              disabled={!(data.year && data.make)}
              onChange={onChange}
              value={data.model}
              options={modelsData.data || []}
              required
              optionObjectKeyName="name"
              optionObjectValueName={optionObjectValueName}
            />
          </div>
          <div className="add-vehicle-half-item">
            <FloatingSelect
              name="trimIndex"
              label="Trim"
              disabled={!(data.year && data.make && data.model)}
              onChange={onChange}
              value={data.trimIndex}
              options={trimsData.data || []}
              required
              optionObjectKeyName="name"
            />
          </div>
        </div>
      )}
      <div className="add-vehicle-row">
        <div className={'add-vehicle-half-item'}>
          <FloatingInput
            label={'MSRP'}
            value={data.msrp}
            type={'number'}
            name={'msrp'}
            max={1000000}
            onChange={onChange}
            min={0}
          />
        </div>
        <div className="add-vehicle-half-item">
          <FloatingInput
            label={'Selling Price'}
            type={'number'}
            max={1000000}
            value={data.price}
            name={'price'}
            onChange={onChange}
            min={0}
          />
        </div>
      </div>
      <div className="add-vehicle-row button-group">
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary cc-button-outlined"
          disabled={isProcessing}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary cc-button-primary"
          disabled={isProcessing}
        >
          {isEmbeddedInCommandCenter() ? 'Add vehicle' : 'Add'}
        </button>
      </div>
    </form>
  );
}

export default AddVehicleWithoutVin;
